import { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { profile } from '../../data/site';
import { LinkedInIcon, MailIcon, PhoneIcon } from '../icons';

const EMPTY = { FirstName: '', Email: '', Subject: '', Message: '' };

const channels = [
  { label: 'Email',    value: profile.email, href: `mailto:${profile.email}`, Icon: MailIcon },
  { label: 'Phone',    value: profile.phone, href: `tel:${profile.phone.replace(/\s/g, '')}`, Icon: PhoneIcon },
  { label: 'LinkedIn', value: '/in/dikshitpatoliya', href: profile.linkedin, Icon: LinkedInIcon },
];

/*--------------------
* Contact Section
----------------------*/
export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const update = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((err) => ({ ...err, [key]: false }));
  };

  const validate = () => {
    const next = {
      FirstName: !form.FirstName.trim(),
      Email: !/^\S+@\S+\.\S+$/.test(form.Email.trim()),
      Message: form.Message.trim().length < 10,
    };
    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!validate()) {
      setStatus('idle');
      return;
    }

    setStatus('sending');
    try {
      await addDoc(collection(db, 'Client'), {
        FirstName: form.FirstName.trim(),
        Email: form.Email.trim(),
        Subject: form.Subject.trim(),
        Message: form.Message.trim(),
        createdAt: serverTimestamp(),
      });
      setForm(EMPTY);
      setStatus('sent');
    } catch (err) {
      console.error('Contact form failed:', err);
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="section relative overflow-hidden">
      <div
        className="aurora h-[460px] w-[460px] bg-violet-brand/15"
        style={{ top: '10%', left: '-160px' }}
        aria-hidden="true"
      />
      <div
        className="aurora h-[380px] w-[380px] bg-cyan-brand/12"
        style={{ bottom: '0', right: '-140px' }}
        aria-hidden="true"
      />

      <div className="container relative z-[1]">
        <div className="reveal max-w-[720px]">
          <span className="kicker">Contact</span>
          <h2 className="section-title mt-4">Let&rsquo;s build something.</h2>
          <p className="lede mt-5">
            Tell me about the project what it does, where it&rsquo;s at, and when you need
            it live. I reply to everything within one working day.
          </p>
        </div>

        <div className="mt-12 grid gap-5 lg:mt-16 lg:grid-cols-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <form
              className="reveal glass card-sheen p-6 lg:p-8"
              onSubmit={sendMessage}
              noValidate
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="name">Your name</label>
                  <input
                    id="name"
                    className={`field ${errors.FirstName ? 'field-error' : ''}`}
                    type="text"
                    placeholder="Jane Cooper"
                    value={form.FirstName}
                    onChange={update('FirstName')}
                  />
                  {errors.FirstName && (
                    <p className="mt-1.5 text-[12.5px] text-pink-brand">Please add your name.</p>
                  )}
                </div>

                <div>
                  <label className="field-label" htmlFor="email">Email</label>
                  <input
                    id="email"
                    className={`field ${errors.Email ? 'field-error' : ''}`}
                    type="email"
                    placeholder="jane@company.com"
                    value={form.Email}
                    onChange={update('Email')}
                  />
                  {errors.Email && (
                    <p className="mt-1.5 text-[12.5px] text-pink-brand">
                      Please add a valid email.
                    </p>
                  )}
                </div>

                <div className="sm:col-span-2">
                  <label className="field-label" htmlFor="subject">
                    Subject <span className="normal-case tracking-normal text-tint/40">(optional)</span>
                  </label>
                  <input
                    id="subject"
                    className="field"
                    type="text"
                    placeholder="AI-powered fitness app, iOS + Android"
                    value={form.Subject}
                    onChange={update('Subject')}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="field-label" htmlFor="message">Project details</label>
                  <textarea
                    id="message"
                    rows="5"
                    className={`field resize-y ${errors.Message ? 'field-error' : ''}`}
                    placeholder="What are you building, what stage is it at, and what's the timeline?"
                    value={form.Message}
                    onChange={update('Message')}
                  />
                  {errors.Message && (
                    <p className="mt-1.5 text-[12.5px] text-pink-brand">
                      A sentence or two about the project, please.
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-4">
                <button
                  className="btn btn-primary disabled:opacity-60"
                  type="submit"
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : 'Send message'}
                  {status !== 'sending' && <span aria-hidden="true">&rarr;</span>}
                </button>

                {status === 'sent' && (
                  <p className="flex items-center gap-2 text-[14px] text-cyan-brand" role="status">
                    <span aria-hidden="true">&#10003;</span>
                    Thanks message received. I&rsquo;ll be in touch shortly.
                  </p>
                )}
                {status === 'error' && (
                  <p className="text-[14px] text-pink-brand" role="status">
                    Something went wrong. Email me directly at{' '}
                    <a className="underline" href={`mailto:${profile.email}`}>
                      {profile.email}
                    </a>
                    .
                  </p>
                )}
              </div>
            </form>
          </div>

          {/* Channels */}
          <div className="lg:col-span-5">
            <div className="reveal glass card-sheen h-full p-6 lg:p-8" style={{ transitionDelay: '.08s' }}>
              <div className="flex items-center gap-2.5">
                <i className="dot-live" />
                <span className="font-mono text-[11px] uppercase tracking-[.16em] text-chalk">
                  {profile.availability}
                </span>
              </div>

              <div className="hairline my-6" />

              <ul className="space-y-3">
                {channels.map((c, i) => (
                  <li key={c.label} className="reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                    <a
                      className="group flex items-center gap-4 rounded-xl border border-tint/[.07] bg-tint/[.02] p-4 transition-all duration-200 hover:-translate-y-[2px] hover:border-violet-brand/40 hover:bg-violet-brand/[.07]"
                      href={c.href}
                      target={c.href.startsWith('http') ? '_blank' : undefined}
                      rel="noreferrer"
                    >
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-brand/25 to-cyan-brand/15 text-chalk"
                        aria-hidden="true"
                      >
                        <c.Icon className="h-[18px] w-[18px]" />
                      </span>
                      <span className="min-w-0">
                        <span className="block font-mono text-[10.5px] uppercase tracking-[.16em] text-muted">
                          {c.label}
                        </span>
                        <span className="block truncate text-[14.5px] text-chalk">{c.value}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>

              <div className="hairline my-6" />

              <p className="text-[14px] leading-[1.65] text-muted">
                Prefer to read first? Grab the full CV every role, project and tool,
                in one page.
              </p>
              <a
                className="btn btn-ghost mt-4 w-full"
                href={`${process.env.PUBLIC_URL}/${profile.cv}`}
                target="_blank"
                rel="noreferrer"
              >
                Download CV <span aria-hidden="true">&darr;</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
